import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function PricingPage() {
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const [stripeStatus, setStripeStatus] = useState({ configured: false });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [stripeRes, subRes] = await Promise.all([
        axios.get(`${API}/stripe/status`).catch(() => ({ data: { configured: false } })),
        token 
          ? axios.get(`${API}/subscriptions/me`, { headers }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null })
      ]);
      
      setStripeStatus(stripeRes.data);
      setCurrentSubscription(subRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSelectPlan = async (tier) => {
    if (!user) {
      // Redirect to register with plan info
      navigate(`/register?plan=${tier}&billing=${isYearly ? 'yearly' : 'monthly'}`);
      return;
    }

    if (!stripeStatus.configured) {
      toast.error('Betalingssystem er ikke konfigurert. Kontakt support.');
      return;
    }

    const packageId = `${tier}_${isYearly ? 'yearly' : 'monthly'}`;
    setProcessingPayment(true);

    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(
        `${API}/stripe/checkout`,
        { 
          package_id: packageId,
          success_url: `${window.location.origin}/subscription?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/pricing`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Kunne ikke starte betaling. Prøv igjen.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const plans = [
    {
      id: 'start',
      name: 'Start',
      level: 'Nivå 1 · Oppfyll kravene',
      levelColor: 'amber',
      description: 'Starter med kravene – bygger videre til kontroll',
      monthlyPrice: 299,
      yearlyPrice: 3049,
      yearlyMonthly: 254,
      features: [
        { text: '1 bedrift', included: true },
        { text: 'Opptil 3 brukere', included: true },
        { text: '10 AI-genereringer/mnd', included: true },
        { text: 'Ferdig internkontrollsystem', included: true },
        { text: 'Full kontroll på avvik', included: true },
        { text: 'Oppgaver og årshjul', included: true },
        { text: 'Word/Excel eksport', included: true },
        { text: 'Miljøfyrtårn- og ISO-forberedelse', included: false },
        { text: 'Ubegrenset AI', included: false },
        { text: 'Prioritert support', included: false },
      ],
      highlighted: false,
      cta: 'Start gratis',
    },
    {
      id: 'pro',
      name: 'Pro',
      level: 'Nivå 2 + 3 · Kontroll i praksis og sertifisering',
      levelColor: 'cyan',
      description: 'Full kontroll – alltid klar for tilsyn og sertifisering',
      monthlyPrice: 549,
      yearlyPrice: 5600,
      yearlyMonthly: 467,
      badge: 'Mest populær',
      features: [
        { text: '1 bedrift', included: true },
        { text: 'Opptil 15 brukere', included: true },
        { text: 'Ubegrenset AI-generering', included: true, highlight: true },
        { text: 'Komplett internkontroll i praksis', included: true },
        { text: 'Full kontroll på avvik', included: true },
        { text: 'Oppgaver og årshjul', included: true },
        { text: 'Ubegrenset eksport', included: true },
        { text: 'Klar for Miljøfyrtårn og ISO', included: true, highlight: true },
        { text: 'GAP-analyse mot 2026-krav', included: true, highlight: true },
        { text: 'Prioritert support', included: true },
      ],
      highlighted: true,
      cta: 'Få full kontroll',
    },
  ];

  const currentPrice = (plan) => isYearly ? plan.yearlyMonthly : plan.monthlyPrice;
  const savings = (plan) => isYearly ? Math.round((1 - plan.yearlyPrice / (plan.monthlyPrice * 12)) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
              alt="Systemklar" 
              className="h-10"
            />
          </Link>
          <div className="flex items-center space-x-4">
            {user ? (
              <Link 
                to="/dashboard" 
                className="text-white/80 hover:text-white transition-colors"
              >
                Til dashboard →
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-white/80 hover:text-white transition-colors">
                  Logg inn
                </Link>
                <Link 
                  to="/register" 
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Registrer deg
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-12 pb-8 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Founding offer badge */}
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full mb-6">
            <span className="text-amber-400 text-sm font-medium">
              ✨ Tidlig tilgangspris – begrenset tid
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Fra krav til kontroll
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> på minutter</span>
          </h1>
          <p className="text-xl text-blue-200/80 mb-4 max-w-2xl mx-auto">
            Fra minimumskrav til full kontroll – i ett system.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-300">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              Nivå 1 · Oppfyll kravene
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-300">
              <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
              Nivå 2 · Kontroll i praksis
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-300">
              <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
              Nivå 3 · Bli sertifisert
            </span>
          </div>
        </div>
      </section>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-12">
        <div className="inline-flex items-center p-1 bg-white/10 backdrop-blur-sm rounded-xl">
          <button
            onClick={() => setIsYearly(false)}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              !isYearly 
                ? 'bg-white text-slate-900 shadow-lg' 
                : 'text-white/70 hover:text-white'
            }`}
          >
            Månedlig
          </button>
          <button
            onClick={() => setIsYearly(true)}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              isYearly 
                ? 'bg-white text-slate-900 shadow-lg' 
                : 'text-white/70 hover:text-white'
            }`}
          >
            Årlig
            <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded-full">
              Spar 15%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <section className="px-4 pb-16">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.highlighted
                  ? 'bg-gradient-to-br from-white to-blue-50 shadow-2xl shadow-blue-500/20 scale-105 border-2 border-blue-400'
                  : 'bg-white/95 backdrop-blur-sm hover:scale-102'
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center px-4 py-1.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-bold rounded-full shadow-lg">
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Current plan indicator */}
              {currentSubscription?.tier === plan.id && (
                <div className="absolute -top-4 right-4">
                  <span className="inline-flex items-center px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                    Ditt abonnement
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                {plan.level && (
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3 ${
                    plan.levelColor === 'amber'
                      ? 'bg-amber-500/10 border border-amber-500/30 text-amber-700'
                      : 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-700'
                  }`} data-testid={`plan-${plan.id}-level-badge`}>
                    <span className={`w-2 h-2 rounded-full ${plan.levelColor === 'amber' ? 'bg-amber-500' : 'bg-cyan-500'}`}></span>
                    {plan.level}
                  </div>
                )}
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-slate-600">{plan.description}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-slate-900">
                    kr {currentPrice(plan)}
                  </span>
                  <span className="text-slate-500">/mnd</span>
                </div>
                {isYearly && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-green-600 font-medium">
                      Spar {savings(plan)}% – kr {plan.yearlyPrice}/år
                    </p>
                    <p className="text-xs text-slate-400 line-through">
                      kr {plan.monthlyPrice * 12}/år med månedlig
                    </p>
                  </div>
                )}
                {!isYearly && (
                  <p className="mt-2 text-sm text-slate-500">
                    Eller kr {plan.yearlyMonthly}/mnd ved årlig betaling
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {feature.included ? (
                      <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-blue-600' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={`text-sm ${feature.included ? (feature.highlight ? 'text-blue-700 font-medium' : 'text-slate-700') : 'text-slate-400'}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={processingPayment || currentSubscription?.tier === plan.id}
                className={`w-full py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  plan.highlighted
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                {currentSubscription?.tier === plan.id 
                  ? 'Aktivt abonnement' 
                  : processingPayment 
                    ? 'Behandler...' 
                    : plan.cta
                }
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Enterprise note */}
      <section className="px-4 pb-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h4 className="text-white font-medium mb-2">Trenger du noe mer avansert?</h4>
            <p className="text-blue-200/70 text-sm mb-4">
              Større organisasjoner, flere bedrifter, eller spesialtilpasninger?
            </p>
            <a 
              href="mailto:post@valoria.no" 
              className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium transition-colors"
            >
              Kontakt oss →
            </a>
          </div>
        </div>
      </section>

      {/* Manual Cost Comparison */}
      <section className="px-4 pb-20" data-testid="manual-cost-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Hva koster det å gjøre dette manuelt?
            </h3>
            <p className="text-blue-200/70 max-w-2xl mx-auto">
              Mange bedrifter bruker mer enn de tror — i timer, penger og risiko. Her er regnestykket.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Manual — Time */}
            <div className="bg-white/5 backdrop-blur-sm border border-red-500/20 rounded-2xl p-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-300 text-xs font-semibold mb-4">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Tid
              </div>
              <div className="text-4xl font-bold text-white mb-2">40+ timer</div>
              <p className="text-sm text-blue-200/70 leading-relaxed">
                Gjennomsnittlig tidsbruk for å etablere et internkontrollsystem fra bunnen — på kvelder og helger.
              </p>
            </div>

            {/* Manual — Consultant */}
            <div className="bg-white/5 backdrop-blur-sm border border-red-500/20 rounded-2xl p-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-300 text-xs font-semibold mb-4">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m3-10h10a2 2 0 012 2v6a2 2 0 01-2 2h-10a2 2 0 01-2-2V7a2 2 0 012-2zm7 5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                Konsulent
              </div>
              <div className="text-4xl font-bold text-white mb-2">15–30 000 kr</div>
              <p className="text-sm text-blue-200/70 leading-relaxed">
                Typisk engangskostnad hos ekstern HMS-konsulent — før løpende oppfølging kommer på toppen.
              </p>
            </div>

            {/* Manual — Risk */}
            <div className="bg-white/5 backdrop-blur-sm border border-red-500/20 rounded-2xl p-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-300 text-xs font-semibold mb-4">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                Risiko
              </div>
              <div className="text-4xl font-bold text-white mb-2">Utdatert</div>
              <p className="text-sm text-blue-200/70 leading-relaxed">
                Permer, Word-filer og gule lapper blir utdatert — og du oppdager det først ved tilsyn eller avvik.
              </p>
            </div>
          </div>

          {/* Systemklar comparison */}
          <div className="bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-cyan-500/5 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-300 text-xs font-semibold mb-4">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Med Systemklar
            </div>
            <h4 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Minutter. Under 10 kr per dag. Alltid oppdatert.
            </h4>
            <p className="text-blue-200/80 max-w-2xl mx-auto">
              Et ferdig system på plass i løpet av en kaffepause — som holder seg levende uke for uke, slik at du aldri trenger å starte forfra igjen.
            </p>
          </div>
        </div>
      </section>


      {/* Trust signals */}
      <section className="px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">Norsk SMB</div>
              <div className="text-sm text-blue-200/60">Laget for små og mellomstore bedrifter</div>
            </div>
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">🇳🇴</div>
              <div className="text-sm text-blue-200/60">Norsk løsning og support</div>
            </div>
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">GDPR</div>
              <div className="text-sm text-blue-200/60">Trygg datahåndtering</div>
            </div>
            <div className="p-4">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">Norsk lov</div>
              <div className="text-sm text-blue-200/60">Bygget på norsk regelverk</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 pb-20">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-white text-center mb-8">Ofte stilte spørsmål</h3>
          <div className="space-y-4">
            <FAQItem 
              question="Kan jeg bytte plan senere?"
              answer="Ja, du kan oppgradere eller nedgradere når som helst. Ved oppgradering får du umiddelbart tilgang til nye funksjoner."
            />
            <FAQItem 
              question="Hva skjer med dataene mine hvis jeg kansellerer?"
              answer="Du beholder lesetilgang til dataene dine i 30 dager etter kansellering. Du kan eksportere alt innhold når som helst."
            />
            <FAQItem 
              question="Er det noen bindingstid?"
              answer="Nei, ingen bindingstid. Du kan kansellere månedlige abonnementer når som helst. Årlige abonnementer fornyes ikke automatisk."
            />
            <FAQItem 
              question="Hvordan fungerer AI-genereringene?"
              answer="AI-generering hjelper deg å lage internkontrollsystem, risikovurderinger, og prosedyrer basert på din bedriftstype og bransje."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/eui6rlb9_Hovedlogo%20Valoria%20%28uten%20bakgrunn%29.png" 
              alt="Valoria Solutions" 
              className="h-6 opacity-60"
            />
            <span className="text-white/40 text-sm">© 2026 Valoria Solutions AS</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="text-white/50 hover:text-white/80 transition-colors">Vilkår</Link>
            <Link to="/privacy" className="text-white/50 hover:text-white/80 transition-colors">Personvern</Link>
            <Link to="/disclaimer" className="text-white/50 hover:text-white/80 transition-colors">Ansvarsfraskrivelse</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left"
      >
        <span className="font-medium text-white">{question}</span>
        <svg 
          className={`w-5 h-5 text-white/50 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-blue-200/70">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default PricingPage;
